import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Play } from "lucide-react";

const FLAG = "st_duty_alert_pending";

const getCurrentPosition = () => new Promise((resolve) => {
  if (typeof navigator === "undefined" || !navigator.geolocation) {
    resolve({ reason: "unsupported" });
    return;
  }
  navigator.geolocation.getCurrentPosition(
    (pos) => resolve({ latitude: pos.coords.latitude, longitude: pos.coords.longitude }),
    (err) => resolve({ reason: err.code === 1 ? "denied" : "unavailable" }),
    { enableHighAccuracy: true, timeout: 8000, maximumAge: 60000 }
  );
});

export const DutyLoginAlert = () => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user) return;
    let pending = false;
    try { pending = sessionStorage.getItem(FLAG) === "1"; } catch { /* ignore */ }
    if (!pending) return;
    try { sessionStorage.removeItem(FLAG); } catch { /* ignore */ }
    if (!user.duty_alert_enabled) return;
    // Only prompt when there's no duty session already open.
    api.get("/duty/current")
      .then((r) => { if (!r.data?.active) setOpen(true); })
      .catch(() => { /* offline — skip the prompt */ });
  }, [user]);

  const startDuty = async () => {
    setBusy(true);
    try {
      const pos = await getCurrentPosition();
      if (pos.latitude == null || pos.longitude == null) {
        toast.error("Location is required to start duty — turn on GPS, then use Start duty on the Clock In page.");
        setOpen(false);
        return;
      }
      const { data } = await api.post("/duty/start", {
        client_latitude: pos.latitude,
        client_longitude: pos.longitude,
        geofence_bypass_reason: null,
      });
      window.dispatchEvent(new Event("st:ping-now"));
      window.dispatchEvent(new Event("st:duty-changed"));
      toast.success("On duty. Shift log stamped.");
      if (data.geofence_bypassed && data.geofence_reason === "outside" && data.geofence_distance_km != null) {
        toast.warning(`Flagged: you're ${data.geofence_distance_km} km from your city center.`);
      }
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start duty");
      setOpen(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={(v) => { if (!busy) setOpen(v); }}>
      <AlertDialogContent data-testid="duty-login-alert">
        <AlertDialogHeader>
          <AlertDialogTitle>Start duty?</AlertDialogTitle>
          <AlertDialogDescription>
            You're logged in but not on duty yet. Starting duty stamps your shift log with GPS + timestamp — no hours are recorded.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy} data-testid="duty-login-alert-dismiss">Not now</AlertDialogCancel>
          <Button
            className="bg-summit-primary hover:bg-summit-primary-soft text-white"
            onClick={startDuty}
            disabled={busy}
            data-testid="duty-login-alert-start"
          >
            <Play className="w-4 h-4 mr-1.5" /> {busy ? "Starting…" : "Start duty"}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default DutyLoginAlert;
